/**
 * English UI strings — the source of truth every other locale mirrors.
 *
 * Headings that contain a gradient word are split into `pre` / `accent` /
 * `post` so translations can move the highlighted word anywhere in the
 * sentence. Spacing lives inside the strings.
 */
const en = {
  meta: {
    home: {
      title: 'Manifestify | Manifestation & Daily Affirmations App',
      description:
        'Manifestify is a manifestation and daily affirmations app for iOS and Android. Build your personal manifesto, set intentions, create vision boards and decode your dreams with AI.',
    },
    blog: {
      title: 'Manifestation Blog: Affirmations, Guides & Methods',
      description:
        'Expert guides on how to manifest: daily affirmations, the 369 method, vision boards, scripting and dream interpretation. New posts from the Manifestify team.',
    },
    notFound: {
      title: 'Page Not Found | Manifestify',
      description: 'The page you are looking for does not exist.',
    },
    terms: {
      title: 'Terms & Conditions | Manifestify',
      description: 'The terms and conditions for using the Manifestify app and website.',
    },
    privacy: {
      title: 'Privacy Policy | Manifestify',
      description: 'How Manifestify collects, uses and protects your personal data.',
    },
  },

  nav: {
    brandAria: 'Manifestify home',
    mainAria: 'Main navigation',
    blog: 'Blog',
    getApp: 'Get the app',
    languageAria: 'Change language',
    languageLabel: 'Language',
  },

  store: {
    downloadOn: 'Download on the',
    appStore: 'App Store',
    getItOn: 'GET IT ON',
    googlePlay: 'Google Play',
    appleAria: 'Download Manifestify on the App Store',
    googleAria: 'Get Manifestify on Google Play',
  },

  home: {
    hero: {
      titleLine1: 'Manifest your dreams, daily.',
      titleAccent: 'With Manifestify!',
      subtitle:
        'The manifestation app that keeps your practice alive: daily affirmations, a personal manifesto, vision boards, intentions and AI dream interpretation, all in one place. Repeat. Believe. Become.',
      note: 'Free to download · iOS & Android',
    },
    features: {
      titlePre: 'Everything you need to ',
      titleAccent: 'manifest',
      titlePost: '',
      subtitle:
        "One app for your whole practice, from your first morning affirmation to decoding last night's dream.",
      items: [
        {
          title: 'Daily Affirmations',
          text: 'Hundreds of carefully crafted affirmations for love, money, health, confidence and inner peace, delivered to you every day.',
        },
        {
          title: 'Personal Manifesto',
          text: 'Turn your goals into a personal manifesto you read and reinforce daily, so your dreams stay in front of you.',
        },
        {
          title: 'Intention Setting',
          text: 'Set clear daily intentions and let the app keep you anchored to what actually matters to you.',
        },
        {
          title: 'Vision Board',
          text: 'Build a digital vision board of the life you are creating and visualize it anywhere, anytime.',
        },
        {
          title: 'AI Dream Interpretation',
          text: 'Describe your dream and discover its hidden messages with thoughtful AI-powered insights.',
        },
        {
          title: 'Energy Cards',
          text: 'Draw a daily energy card for a moment of reflection and a fresh dose of inspiration.',
        },
        {
          title: 'Daily Reminders',
          text: 'Gentle notifications at the times you choose, so your practice never depends on memory or motivation.',
        },
        {
          title: 'Themes & Wallpapers',
          text: 'Beautiful themes and wallpapers that make your practice feel personal and inspiring.',
        },
      ],
    },
    how: {
      titlePre: 'How it ',
      titleAccent: 'works',
      titlePost: '',
      steps: [
        {
          title: 'Set your intention',
          text: 'Choose what you want to manifest and build your personal manifesto from hundreds of curated affirmations.',
        },
        {
          title: 'Practice daily',
          text: 'Repeat your affirmations, visualize your vision board and draw your energy card. Reminders keep your streak alive.',
        },
        {
          title: 'Believe and become',
          text: 'Watch your focus, confidence and choices shift as your practice compounds day after day.',
        },
      ],
    },
    blogTeaser: {
      titlePre: 'Learn the art of ',
      titleAccent: 'manifestation',
      titlePost: '',
      subtitle: 'Guides and techniques from the Manifestify team, written for real daily practice.',
      viewAll: 'View all articles →',
    },
    faq: {
      titlePre: 'Frequently asked ',
      titleAccent: 'questions',
      titlePost: '',
      items: [
        {
          q: 'What is Manifestify?',
          a: 'Manifestify is a manifestation and daily affirmations app for iOS and Android. It brings your entire practice together: affirmations across categories like love, career, wealth and confidence, a personal manifesto, intention setting, a vision board, daily reminders, energy cards and AI-powered dream interpretation.',
        },
        {
          q: 'Is Manifestify free?',
          a: 'Yes. Manifestify is free to download and use on both the App Store and Google Play. Optional premium plans unlock the full affirmation library and advanced features like unlimited AI dream interpretation.',
        },
        {
          q: 'What is manifestation?',
          a: 'Manifestation is the practice of bringing your goals into reality through focused attention, positive belief and consistent action. Techniques include daily affirmations, visualization, vision boards, scripting and the 369 method.',
        },
        {
          q: 'How do daily affirmations work?',
          a: 'Affirmations are short, present-tense positive statements you repeat every day. Over weeks, they reshape your self-talk and train your attention toward opportunities aligned with your goals. Consistency is the key, which is why Manifestify includes daily reminders.',
        },
        {
          q: 'Is Manifestify available on iPhone and Android?',
          a: 'Yes. Manifestify is available for iPhone and iPad on the App Store, and for Android devices on Google Play.',
        },
        {
          q: 'How does the AI dream interpretation feature work?',
          a: 'You describe your dream in the app, and Manifestify analyzes its symbols, emotions and themes to give you an instant, thoughtful interpretation you can refine with your own context.',
        },
      ],
    },
    download: {
      title: 'Start manifesting today',
      text: 'Join the people who begin every morning with intention. Download Manifestify free and build a practice that sticks.',
    },
  },

  blog: {
    titlePre: 'The Manifestify ',
    titleAccent: 'Blog',
    titlePost: '',
    subtitle:
      'Practical guides on manifestation, affirmations, visualization and dream interpretation. Everything you need to build a daily practice that works.',
    schemaName: 'The Manifestify Blog',
    schemaDescription:
      'Guides on manifestation, daily affirmations, vision boards, scripting and dream interpretation.',
    readTime: '{n} min read',
  },

  post: {
    breadcrumbAria: 'Breadcrumb',
    home: 'Home',
    blog: 'Blog',
    ctaTitle: 'Put it into practice with Manifestify',
    ctaText:
      'Daily affirmations, a personal manifesto, vision boards, intentions and AI dream interpretation. Your whole manifestation practice, in one free app.',
    faqTitle: 'Frequently asked questions',
    relatedTitle: 'Keep reading',
    relatedAria: 'Related articles',
  },

  notFound: {
    text: "This page doesn't exist, but your dreams still do.",
    backHome: 'Back to home',
    readBlog: 'Read the blog',
  },

  footer: {
    tagline:
      'Manifest your dreams, daily. Affirmations, intentions, vision boards and dream interpretation in one app.',
    exploreTitle: 'Explore',
    exploreAria: 'Explore',
    home: 'Home',
    blog: 'Blog',
    downloadTitle: 'Get the app',
    downloadAria: 'Download',
    appStoreLink: 'iPhone & iPad (App Store)',
    playStoreLink: 'Android (Google Play)',
    legalTitle: 'Legal',
    legalAria: 'Legal',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    copyright: '© {year} Manifestify by Portal Studio. All rights reserved.',
  },

  legal: {
    back: 'Back',
    /** Shown on translated legal pages only; the English original governs. */
    translationNotice:
      'This is a translation provided for convenience. The English version of this document is the legally binding one.',
    lastUpdated: 'Last updated: {date}',
  },
};

export default en;
